import React, { useContext, useEffect, PropsWithChildren } from 'react';
import globalvarsConfig from 'globalvars.config';
import { User } from 'oidc-client-ts';
import { AuthContext, AuthProvider, hasAuthParams } from 'react-oidc-context';
import ApolloLinkTimeout from 'apollo-link-timeout';
import { setContext } from '@apollo/client/link/context';
import { useUrlSessionStorage } from 'utilities/session';

export default function AuthWrapper({ children }: PropsWithChildren<{}>) {
    const { url } = useUrlSessionStorage();

    const oidcConfig = globalvarsConfig.SsoConfig ? {
        authority: globalvarsConfig.SsoConfig.Authority,
        client_id: globalvarsConfig.SsoConfig.ClientId,
        redirect_uri: globalvarsConfig.SsoConfig.RedirectUri,
        scope: globalvarsConfig.SsoConfig.Scope,
        campaign_visualisation: globalvarsConfig.CampaignVisualisationActive,
        onSigninCallback: (_user: User | void): void => {
            window.location.replace(url);
        }
    } : null;

    if (oidcConfig) {
        return (
            <AuthProvider {...oidcConfig}>
                {children}
            </AuthProvider>
        );
    }
    return <>{children}</>;
}

export function useAuthLink() {
    const auth = useContext(AuthContext);

    const timeoutLink = new ApolloLinkTimeout(600000); // 10 min timeout

    if (auth) {
        const authLink = setContext((_, { headers }) => {
            const token = auth.user?.access_token;
            // return the headers to the context so httpLink can read them
            return {
                headers: {
                    ...headers,
                    authorization: token ? `Bearer ${token}` : '',
                },
            };
        });
        return {
            link: authLink.concat(timeoutLink),
        };
    } else return {
        link: timeoutLink,
    };
}

export function useAuthRedirect() {
    const { setUrl } = useUrlSessionStorage();
    const auth = useContext(AuthContext);
    const isLoading = auth?.activeNavigator || auth?.isLoading;
    const isAuthenticated = auth?.isAuthenticated;

    const updateSessionStorageAndRedirect = () => {
        setUrl(window.location.pathname);
        auth?.signinRedirect();
    }

    useEffect(() => {
        if (auth) {
            return auth.events.addAccessTokenExpiring(() => {
                auth.signinSilent().catch(updateSessionStorageAndRedirect);
            });
        }
    }, [auth?.events, auth?.signinSilent, auth?.signinRedirect])

    useEffect(() => {
        if (auth && !hasAuthParams() &&
            !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading) {
            updateSessionStorageAndRedirect();
        }
    }, [auth?.isAuthenticated, auth?.activeNavigator, auth?.isLoading, auth?.signinRedirect]);

    return [isLoading, isAuthenticated];
}